import { useState, useCallback, useRef, useEffect } from 'react';
import { AppNode } from '@/types';
import { toast } from 'sonner';

export interface UseNodeMediaOptions {
  data: AppNode;
  shouldPlay?: boolean;
}

export function useNodeMedia(options: UseNodeMediaOptions) {
  const { data, shouldPlay = false } = options;
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [showImageGrid, setShowImageGrid] = useState(false);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoVolume, setVideoVolume] = useState(0.5);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [hasAudioTrack, setHasAudioTrack] = useState(false);
  const mediaRef = useRef<HTMLImageElement | HTMLVideoElement | HTMLAudioElement | null>(null);
  const prevShouldPlayRef = useRef(shouldPlay);

  // 根据 shouldPlay（hover || selected）自动播放/暂停视频
  useEffect(() => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;

    if (shouldPlay && video.paused) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlayingVideo(true))
          .catch(() => {
            // 浏览器阻止带声音自动播放，降级为静音播放
            if (!video.muted) {
              video.muted = true;
              setIsVideoMuted(true);
              video.play().then(() => setIsPlayingVideo(true)).catch(() => {});
              toast.info('已静音播放，点击音量图标开启声音', { duration: 2000 });
            }
          });
      }
    } else if (!shouldPlay && !video.paused) {
      video.pause();
      setIsPlayingVideo(false);
    }

    prevShouldPlayRef.current = shouldPlay;
  }, [shouldPlay]);

  // 检测视频是否有音频轨道
  useEffect(() => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) {
      setHasAudioTrack(false);
      return;
    }

    let detected = false;

    const checkAudio = () => {
      if (detected) return;
      const v = video as HTMLVideoElement & {
        mozHasAudio?: boolean;
        webkitAudioDecodedByteCount?: number;
        audioTracks?: { length: number };
        captureStream?: () => MediaStream;
      };

      let hasCaptureAudio = false;
      try {
        if (typeof v.captureStream === 'function') {
          const stream = v.captureStream();
          hasCaptureAudio = stream.getAudioTracks().length > 0;
        }
      } catch {
        // captureStream 在某些浏览器/状态下不可用，忽略
      }

      const hasAudio =
        v.mozHasAudio === true ||
        (typeof v.webkitAudioDecodedByteCount === 'number' && v.webkitAudioDecodedByteCount > 0) ||
        (v.audioTracks && v.audioTracks.length > 0) ||
        hasCaptureAudio;
      if (hasAudio) {
        detected = true;
        setHasAudioTrack(true);
      }
    };

    const events: (keyof HTMLMediaElementEventMap)[] = ['loadedmetadata', 'loadeddata', 'canplay', 'playing', 'timeupdate'];
    events.forEach((evt) => video.addEventListener(evt, checkAudio));

    if (video.readyState >= 1) {
      checkAudio();
    }

    return () => {
      events.forEach((evt) => video.removeEventListener(evt, checkAudio));
    };
  }, [data.data.videoUri]);

  // 同步 video 元素的 volume / muted 到状态
  useEffect(() => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    video.volume = videoVolume;
    video.muted = isVideoMuted;
  }, [videoVolume, isVideoMuted]);

  const handleMouseEnter = useCallback(() => {
    if (data.data.images && data.data.images.length > 1 || (data.data.videoUris && data.data.videoUris.length > 1)) {
      setShowImageGrid(true);
    }
  }, [data.data.images, data.data.videoUris]);

  const handleMouseLeave = useCallback(() => {
    setShowImageGrid(false);
  }, []);

  const toggleVideoPlay = useCallback(() => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.paused) {
      video.play().then(() => setIsPlayingVideo(true)).catch(() => {});
    } else {
      video.pause();
      setIsPlayingVideo(false);
    }
  }, []);

  const toggleVideoMute = useCallback(() => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    const next = !isVideoMuted;
    video.muted = next;
    setIsVideoMuted(next);
  }, [isVideoMuted]);

  const changeVideoVolume = useCallback((volume: number) => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    const clamped = Math.max(0, Math.min(1, volume));
    video.volume = clamped;
    setVideoVolume(clamped);
    if (clamped > 0 && isVideoMuted) {
      video.muted = false;
      setIsVideoMuted(false);
    } else if (clamped === 0 && !isVideoMuted) {
      video.muted = true;
      setIsVideoMuted(true);
    }
  }, [isVideoMuted]);

  const handleVideoTimeUpdate = useCallback(() => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    setVideoProgress(video.currentTime);
    setVideoDuration(video.duration || 0);
  }, []);

  const handleVideoSeek = useCallback((time: number) => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    video.currentTime = time;
    setVideoProgress(time);
  }, []);

  const handleVideoEnded = useCallback(() => {
    setIsPlayingVideo(false);
    setVideoProgress(0);
  }, []);

  const handleVideoLoadedMetadata = useCallback(() => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    setVideoDuration(video.duration || 0);
  }, []);

  return {
    isPlayingAudio,
    setIsPlayingAudio,
    isPlayingVideo,
    setIsPlayingVideo,
    videoProgress,
    videoDuration,
    videoVolume,
    isVideoMuted,
    hasAudioTrack,
    showImageGrid,
    mediaRef,
    handleMouseEnter,
    handleMouseLeave,
    toggleVideoPlay,
    toggleVideoMute,
    changeVideoVolume,
    handleVideoTimeUpdate,
    handleVideoSeek,
    handleVideoEnded,
    handleVideoLoadedMetadata,
  };
}
